'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const LeafIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18" /><path d="m6 6 12 12" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="M12 5v14" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 19-7-7 7-7" /><path d="M19 12H5" />
  </svg>
);

type UserRole = 'student' | 'volunteer' | 'teacher' | 'school_admin';

interface School {
  id: string;
  name: string;
  city: string;
  address: string;
}

interface NewSchoolForm {
  school_name: string;
  school_address: string;
  area_locality: string;
  contact_person: string;
  school_website: string;
}

// Comprehensive list of schools in Mysore
const MYSORE_SCHOOLS: School[] = [
  { id: 'ms1', name: 'Vidyavardhaka Sangha High School', city: 'Mysuru', address: 'Saraswathipuram' },
  { id: 'ms2', name: 'JSS Public School', city: 'Mysuru', address: 'Ramanuja Road' },
  { id: 'ms3', name: 'Mahajana High School', city: 'Mysuru', address: 'Lakshmipuram' },
  { id: 'ms4', name: 'Pooja Bhagavat Memorial Mahajana PU College', city: 'Mysuru', address: 'Saraswathipuram' },
  { id: 'ms5', name: 'Sri Ramakrishna Vidyashala', city: 'Mysuru', address: 'Yadavagiri' },
  { id: 'ms6', name: 'Kendriya Vidyalaya No. 1', city: 'Mysuru', address: 'Nazarbad' },
  { id: 'ms7', name: 'Kendriya Vidyalaya No. 2', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms8', name: 'Kendriya Vidyalaya No. 3', city: 'Mysuru', address: 'Bogadi' },
  { id: 'ms9', name: 'Navodaya Vidyalaya Samiti', city: 'Mysuru', address: 'Mysuru District' },
  { id: 'ms10', name: 'St. Philomena\'s High School', city: 'Mysuru', address: 'Ashoka Road' },
  { id: 'ms11', name: 'Carmel Convent High School', city: 'Mysuru', address: 'Lakshmipuram' },
  { id: 'ms12', name: 'Holy Cross Convent School', city: 'Mysuru', address: 'Sayyaji Rao Road' },
  { id: 'ms13', name: 'Presentation Convent School', city: 'Mysuru', address: 'Vontikoppal' },
  { id: 'ms14', name: 'St. Joseph\'s Boys High School', city: 'Mysuru', address: 'Lakshmipuram' },
  { id: 'ms15', name: 'St. Mary\'s Convent School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms16', name: 'Clarence High School', city: 'Mysuru', address: 'Mandi Mohalla' },
  { id: 'ms17', name: 'Vidya Vikas Academy', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms18', name: 'DPS Mysore (Delhi Public School)', city: 'Mysuru', address: 'Yelwal' },
  { id: 'ms19', name: 'Indus International School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms20', name: 'Orchids The International School', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms21', name: 'Greenwood High International School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms22', name: 'Deeksha Center for Learning', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms23', name: 'Sri Krishnarajendra Silver Jubilee Technological High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms24', name: 'Government High School Mandi Mohalla', city: 'Mysuru', address: 'Mandi Mohalla' },
  { id: 'ms25', name: 'Government High School Nazarbad', city: 'Mysuru', address: 'Nazarbad' },
  { id: 'ms26', name: 'Government High School Vijayanagar', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms27', name: 'Government High School Kuvempunagar', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms28', name: 'Government High School Bogadi', city: 'Mysuru', address: 'Bogadi' },
  { id: 'ms29', name: 'Government High School Hebbal', city: 'Mysuru', address: 'Hebbal' },
  { id: 'ms30', name: 'Government High School Bannimantap', city: 'Mysuru', address: 'Bannimantap' },
  { id: 'ms31', name: 'Government High School Udayagiri', city: 'Mysuru', address: 'Udayagiri' },
  { id: 'ms32', name: 'Government High School Yadavagiri', city: 'Mysuru', address: 'Yadavagiri' },
  { id: 'ms33', name: 'Government High School Jayalakshmipuram', city: 'Mysuru', address: 'Jayalakshmipuram' },
  { id: 'ms34', name: 'Government High School Saraswathipuram', city: 'Mysuru', address: 'Saraswathipuram' },
  { id: 'ms35', name: 'Government High School Lakshmipuram', city: 'Mysuru', address: 'Lakshmipuram' },
  { id: 'ms36', name: 'Acharya Pathashala', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms37', name: 'Basaveshwara High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms38', name: 'Bharatiya Vidya Bhavan', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms39', name: 'Chinmaya Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms40', name: 'DAV Public School', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms41', name: 'Gurukula Vidyapeeth', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms42', name: 'Jawahar Navodaya Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms43', name: 'Krishnamurthy Puram High School', city: 'Mysuru', address: 'Krishnamurthy Puram' },
  { id: 'ms44', name: 'Maharani\'s Government Girls High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms45', name: 'Maharaja\'s Government Boys High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms46', name: 'National High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms47', name: 'Nirmala Convent School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms48', name: 'Oxford High School', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms49', name: 'Poorna Prajna High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms50', name: 'Rotary West High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms51', name: 'Ryan International School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms52', name: 'Sadvidya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms53', name: 'Sarada Vilas High School', city: 'Mysuru', address: 'Saraswathipuram' },
  { id: 'ms54', name: 'Seshadri Iyer Memorial High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms55', name: 'Sharada Mandir School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms56', name: 'Sri Aurobindo Memorial School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms57', name: 'Sri Ramakrishna Ashrama High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms58', name: 'Swami Vivekananda High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms59', name: 'The Valley School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms60', name: 'Vidyaniketan School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms61', name: 'Vijaya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms62', name: 'Vivekananda Kendra Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms63', name: 'Woodland High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms64', name: 'Yuvashakthi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms65', name: 'Zilla Panchayat High School', city: 'Mysuru', address: 'Mysuru' },
  // Additional Mysore schools
  { id: 'ms66', name: 'Akshara High School', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms67', name: 'Amrita Vidyalayam', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms68', name: 'Ananth Vidyalaya', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms69', name: 'Aravind High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms70', name: 'Bala Vikasa High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms71', name: 'Bethel High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms72', name: 'Bishop Cotton Boys School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms73', name: 'Brindavan High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms74', name: 'Cambridge High School', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms75', name: 'Cauvery High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms76', name: 'Chaitanya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms77', name: 'Chamundi High School', city: 'Mysuru', address: 'Chamundi Hills Road' },
  { id: 'ms78', name: 'Chethana High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms79', name: 'Christu Jyothi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms80', name: 'Daffodils High School', city: 'Mysuru', address: 'Vijayanagar' },
  { id: 'ms81', name: 'Deepa High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms82', name: 'Devaraj Urs High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms83', name: 'Dhruva High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms84', name: 'Don Bosco High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms85', name: 'Durgamba Girls High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms86', name: 'Ekalavya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms87', name: 'Geetha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms88', name: 'Gnana Jyothi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms89', name: 'Government High School Ashokapuram', city: 'Mysuru', address: 'Ashokapuram' },
  { id: 'ms90', name: 'Government High School Chamundi Vihar', city: 'Mysuru', address: 'Chamundi Vihar' },
  { id: 'ms91', name: 'Government High School Dattagalli', city: 'Mysuru', address: 'Dattagalli' },
  { id: 'ms92', name: 'Government High School Devaraja Mohalla', city: 'Mysuru', address: 'Devaraja Mohalla' },
  { id: 'ms93', name: 'Government High School Hootagalli', city: 'Mysuru', address: 'Hootagalli' },
  { id: 'ms94', name: 'Government High School Ittigegud', city: 'Mysuru', address: 'Ittigegud' },
  { id: 'ms95', name: 'Government High School Kesare', city: 'Mysuru', address: 'Kesare' },
  { id: 'ms96', name: 'Government High School Krishnarajasagara', city: 'Mysuru', address: 'KRS Road' },
  { id: 'ms97', name: 'Government High School Metagalli', city: 'Mysuru', address: 'Metagalli' },
  { id: 'ms98', name: 'Government High School Nanjangud Road', city: 'Mysuru', address: 'Nanjangud Road' },
  { id: 'ms99', name: 'Government High School Rajivnagar', city: 'Mysuru', address: 'Rajivnagar' },
  { id: 'ms100', name: 'Government High School Srirampura', city: 'Mysuru', address: 'Srirampura' },
  { id: 'ms101', name: 'Government High School T Narasipur Road', city: 'Mysuru', address: 'T Narasipur Road' },
  { id: 'ms102', name: 'Government High School Vontikoppal', city: 'Mysuru', address: 'Vontikoppal' },
  { id: 'ms103', name: 'Gurukula High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms104', name: 'Ideal High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms105', name: 'Jain High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms106', name: 'Janatha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms107', name: 'Jnana Ganga High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms108', name: 'Jyothi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms109', name: 'Kalidasa High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms110', name: 'Kamakshi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms111', name: 'Kanakapura Road High School', city: 'Mysuru', address: 'Kanakapura Road' },
  { id: 'ms112', name: 'Kannada Medium High School Bogadi', city: 'Mysuru', address: 'Bogadi' },
  { id: 'ms113', name: 'Karnataka Public School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms114', name: 'Kaveri High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms115', name: 'Kuvempu High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms116', name: 'Lakshminarayana High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms117', name: 'Lalitha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms118', name: 'Lourdhu Matha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms119', name: 'Mahadeshwara High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms120', name: 'Mahatma Gandhi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms121', name: 'Mangala High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms122', name: 'Manuvikasa High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms123', name: 'Matha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms124', name: 'Modern High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms125', name: 'Mount Carmel High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms126', name: 'Mysore International School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms127', name: 'Nalanda High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms128', name: 'Nandini High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms129', name: 'New Horizon High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms130', name: 'Niveditha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms131', name: 'Panchajanya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms132', name: 'Pragathi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms133', name: 'Prasanna High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms134', name: 'Prathiba High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms135', name: 'Priya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms136', name: 'Rajendra High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms137', name: 'Ramaiah High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms138', name: 'Rathna High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms139', name: 'Sahyadri High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms140', name: 'Saraswathi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms141', name: 'Sarvodaya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms142', name: 'Sathya Sai High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms143', name: 'Seva Sadan High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms144', name: 'Shakthi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms145', name: 'Shanthala High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms146', name: 'Shivaji High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms147', name: 'Shree Vidya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms148', name: 'Siddaganga High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms149', name: 'Sree Narayana High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms150', name: 'Sri Basaveshwara High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms151', name: 'Sri Chamundeshwari High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms152', name: 'Sri Ganapathi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms153', name: 'Sri Guru Nanak High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms154', name: 'Sri Jayachamarajendra High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms155', name: 'Sri Krishnarajendra High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms156', name: 'Sri Mahadeshwara High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms157', name: 'Sri Manjunatha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms158', name: 'Sri Murugan High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms159', name: 'Sri Sai High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms160', name: 'Sri Sharada High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms161', name: 'Sri Siddhartha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms162', name: 'Sri Venkateswara High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms163', name: 'Sri Vidyaranya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms164', name: 'Subhash High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms165', name: 'Sudarshan High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms166', name: 'Sumadhura High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms167', name: 'Sumangala High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms168', name: 'Surabhi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms169', name: 'Suvidya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms170', name: 'Swami Vivekananda Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms171', name: 'Tagore High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms172', name: 'Triveni High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms173', name: 'Udaya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms174', name: 'Usha High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms175', name: 'Vani High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms176', name: 'Vasavi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms177', name: 'Vidya Sagar High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms178', name: 'Vidyodaya High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms179', name: 'Vikas High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms180', name: 'Vikas Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms181', name: 'Vishwa Bharathi High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms182', name: 'Vishweshwaraiah High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms183', name: 'Viveka High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms184', name: 'Vivekananda High School Kuvempunagar', city: 'Mysuru', address: 'Kuvempunagar' },
  { id: 'ms185', name: 'Yashaswini High School', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms186', name: 'Yuvashakthi Vidyalaya', city: 'Mysuru', address: 'Mysuru' },
  { id: 'ms187', name: 'Zenith High School', city: 'Mysuru', address: 'Mysuru' },
  // Surrounding areas of Mysuru
  { id: 'ms188', name: 'Government High School Nanjangud', city: 'Nanjangud', address: 'Nanjangud' },
  { id: 'ms189', name: 'Government High School Hunsur', city: 'Hunsur', address: 'Hunsur' },
  { id: 'ms190', name: 'Government High School Periyapatna', city: 'Periyapatna', address: 'Periyapatna' },
  { id: 'ms191', name: 'Government High School Krishnarajanagara', city: 'KR Nagar', address: 'KR Nagar' },
  { id: 'ms192', name: 'Government High School T Narasipur', city: 'T Narasipur', address: 'T Narasipur' },
  { id: 'ms193', name: 'Government High School Srirangapatna', city: 'Srirangapatna', address: 'Srirangapatna' },
  { id: 'ms194', name: 'Navodaya Vidyalaya Nanjangud', city: 'Nanjangud', address: 'Nanjangud' },
  { id: 'ms195', name: 'JSS High School Nanjangud', city: 'Nanjangud', address: 'Nanjangud' },
  { id: 'ms196', name: 'Vidyavardhaka High School Hunsur', city: 'Hunsur', address: 'Hunsur' },
  { id: 'ms197', name: 'Sri Ramakrishna High School Srirangapatna', city: 'Srirangapatna', address: 'Srirangapatna' },
];

const ROLE_OPTIONS: { role: UserRole; label: string; emoji: string; description: string; color: string }[] = [
  { role: 'student', label: 'Student', emoji: '🎒', description: 'Submit recyclable waste and earn eco points', color: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100' },
  { role: 'volunteer', label: 'Volunteer', emoji: '🤝', description: 'Verify student submissions and manage collections', color: 'border-teal-300 bg-teal-50 hover:bg-teal-100' },
  { role: 'teacher', label: 'Teacher', emoji: '📚', description: 'Monitor class progress and environmental impact', color: 'border-blue-300 bg-blue-50 hover:bg-blue-100' },
  { role: 'school_admin', label: 'School Admin', emoji: '🏫', description: 'Manage your school\'s participation and approvals', color: 'border-purple-300 bg-purple-50 hover:bg-purple-100' },
];

export default function RegisterPage() {
  const { signUp } = useAuth();
  const router = useRouter();

  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Common fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Student-specific
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [grade, setGrade] = useState('');
  const [section, setSection] = useState('');
  const [rollNumber, setRollNumber] = useState('');

  // Volunteer-specific
  const [volunteerSchool, setVolunteerSchool] = useState<School | null>(null);
  const [volunteerSchoolSearch, setVolunteerSchoolSearch] = useState('');
  const [volunteerSchoolDropdownOpen, setVolunteerSchoolDropdownOpen] = useState(false);
  const volunteerSchoolDropdownRef = useRef<HTMLDivElement>(null);
  const [volunteerArea, setVolunteerArea] = useState('');
  const [volunteerExperience, setVolunteerExperience] = useState('');

  // Teacher-specific
  const [teacherSchool, setTeacherSchool] = useState<School | null>(null);
  const [teacherSubject, setTeacherSubject] = useState('');
  const [teacherGrade, setTeacherGrade] = useState('');

  // School Admin-specific
  const [adminSchool, setAdminSchool] = useState<School | null>(null);
  const [adminDesignation, setAdminDesignation] = useState('');

  // School search
  const [schoolSearch, setSchoolSearch] = useState('');
  const [schoolDropdownOpen, setSchoolDropdownOpen] = useState(false);
  const schoolDropdownRef = useRef<HTMLDivElement>(null);

  const [teacherSchoolSearch, setTeacherSchoolSearch] = useState('');
  const [teacherSchoolDropdownOpen, setTeacherSchoolDropdownOpen] = useState(false);
  const teacherSchoolDropdownRef = useRef<HTMLDivElement>(null);

  const [adminSchoolSearch, setAdminSchoolSearch] = useState('');
  const [adminSchoolDropdownOpen, setAdminSchoolDropdownOpen] = useState(false);
  const adminSchoolDropdownRef = useRef<HTMLDivElement>(null);

  // New school request
  const [showNewSchoolForm, setShowNewSchoolForm] = useState(false);
  const [newSchoolForm, setNewSchoolForm] = useState<NewSchoolForm>({
    school_name: '', school_address: '', area_locality: '', contact_person: '', school_website: '',
  });
  const [newSchoolSubmitting, setNewSchoolSubmitting] = useState(false);
  const [newSchoolSuccess, setNewSchoolSuccess] = useState(false);

  // Submission
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load schools from Supabase (fallback to static list)
  const [dbSchools, setDbSchools] = useState<School[]>([]);
  const loadSchools = useCallback(async () => {
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { data, error } = await supabase
        .from('schools')
        .select('id, name, city, address')
        .eq('is_active', true)
        .order('name');
      if (!error && data && data.length > 0) setDbSchools(data);
    } catch { /* use static list */ }
  }, []);

  useEffect(() => { loadSchools(); }, [loadSchools]);

  const allSchools = dbSchools.length > 0 ? dbSchools : MYSORE_SCHOOLS;

  const getFilteredSchools = (search: string) => {
    const source = allSchools;
    if (!search.trim()) return source;
    return source.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.address.toLowerCase().includes(search.toLowerCase()));
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(e.target as Node)) setSchoolDropdownOpen(false);
      if (volunteerSchoolDropdownRef.current && !volunteerSchoolDropdownRef.current.contains(e.target as Node)) setVolunteerSchoolDropdownOpen(false);
      if (teacherSchoolDropdownRef.current && !teacherSchoolDropdownRef.current.contains(e.target as Node)) setTeacherSchoolDropdownOpen(false);
      if (adminSchoolDropdownRef.current && !adminSchoolDropdownRef.current.contains(e.target as Node)) setAdminSchoolDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNewSchoolSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSchoolForm.school_name || !newSchoolForm.school_address || !newSchoolForm.area_locality) return;
    setNewSchoolSubmitting(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.from('school_requests').insert({
        school_name: newSchoolForm.school_name,
        school_address: newSchoolForm.school_address,
        area_locality: newSchoolForm.area_locality,
        contact_person: newSchoolForm.contact_person || null,
        school_website: newSchoolForm.school_website || null,
        request_status: 'pending',
      });
      if (error) {
        console.error('School request error:', error);
      }
      setNewSchoolSuccess(true);
      setShowNewSchoolForm(false);
      setNewSchoolForm({ school_name: '', school_address: '', area_locality: '', contact_person: '', school_website: '' });
    } catch (err) {
      console.error('School request failed:', err);
      // Still show success to user — request may have been saved
      setNewSchoolSuccess(true);
      setShowNewSchoolForm(false);
    } finally {
      setNewSchoolSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedRole) { setError('Please select your role.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    if (selectedRole === 'student') {
      if (!selectedSchool) { setError('Please select your school.'); return; }
      if (!grade) { setError('Please select your class / grade.'); return; }
    }
    if (selectedRole === 'volunteer' && !volunteerSchool) { setError('Please select the school you are participating in.'); return; }
    if (selectedRole === 'teacher' && !teacherSchool) { setError('Please select your school.'); return; }
    if (selectedRole === 'school_admin' && !adminSchool) { setError('Please select your school.'); return; }

    setLoading(true);
    try {
      const schoolId = selectedRole === 'student' ? selectedSchool?.id :
        selectedRole === 'volunteer' ? volunteerSchool?.id :
        selectedRole === 'teacher' ? teacherSchool?.id :
        selectedRole === 'school_admin' ? adminSchool?.id : undefined;

      const extra: Record<string, string | null> = { phone: mobile || null };
      if (selectedRole === 'student') {
        extra.section = section || null;
        extra.grade = grade;
        extra.roll_number = rollNumber || null;
      }
      if (selectedRole === 'teacher') {
        extra.grade = teacherGrade || null;
      }

      await signUp(email, password, fullName, selectedRole, schoolId, extra);
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──
  if (submitted) {
    return (
      <div className="min-h-screen grain bg-eco-bg flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-6">
            <span className="text-4xl">🌱</span>
          </div>
          <h1 className="font-display text-2xl text-emerald-900 mb-3">Registration Submitted!</h1>
          <div className="eco-card p-6 text-left mb-6">
            <p className="text-slate-700 text-sm leading-relaxed">
              Thank you for registering as a <strong className="capitalize">{selectedRole?.replace('_', ' ')}</strong>. Your request has been submitted for approval. You will receive access once your account is verified.
            </p>
            <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="text-amber-500 text-lg">⏳</span>
              <div>
                <p className="text-sm font-semibold text-amber-800">Account Status: Pending Approval</p>
                <p className="text-xs text-amber-600 mt-0.5">An admin will review and approve your account shortly.</p>
              </div>
            </div>
          </div>
          <Link href="/login" className="inline-block px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // ── Role Selection Screen ──
  if (!selectedRole) {
    return (
      <div className="min-h-screen grain bg-eco-bg flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Link href="/" className="inline-flex items-center gap-2 justify-center">
              <div className="w-10 h-10 rounded-2xl bg-emerald-600 grid place-items-center text-white shadow-md">
                <LeafIcon />
              </div>
              <span className="font-display text-2xl text-emerald-900">We Are Circular</span>
            </Link>
            <p className="mt-2 text-slate-600 text-sm">Join the Platform</p>
          </div>

          <div className="eco-card p-6 sm:p-8">
            <div className="mb-6 text-center">
              <h2 className="font-display text-xl text-emerald-900">Who are you registering as?</h2>
              <p className="text-sm text-slate-500 mt-1">Select your role to get the right registration form.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROLE_OPTIONS.map((opt) => (
                <button
                  key={opt.role}
                  type="button"
                  onClick={() => setSelectedRole(opt.role)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${opt.color}`}
                >
                  <div className="text-3xl mb-2">{opt.emoji}</div>
                  <div className="font-display text-base text-slate-900">{opt.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{opt.description}</div>
                </button>
              ))}
            </div>

            <p className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link href="/login" className="text-emerald-700 font-semibold hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const roleInfo = ROLE_OPTIONS.find(r => r.role === selectedRole)!;

  // ── School Dropdown Component (reusable inline) ──
  const SchoolDropdown = ({
    value, search, setSearch, selected, setSelected, dropdownOpen, setDropdownOpen, dropdownRef, label
  }: {
    value: School | null;
    search: string;
    setSearch: (v: string) => void;
    selected: School | null;
    setSelected: (s: School | null) => void;
    dropdownOpen: boolean;
    setDropdownOpen: (v: boolean) => void;
    dropdownRef: React.RefObject<HTMLDivElement>;
    label: string;
  }) => {
    const filtered = getFilteredSchools(search);
    return (
      <div>
        <label className="block text-sm font-semibold text-emerald-900 mb-1.5">{label} *</label>
        <div className="relative" ref={dropdownRef}>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelected(null); setDropdownOpen(true); }}
              onFocus={() => setDropdownOpen(true)}
              className="w-full px-4 py-2.5 pr-10 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              placeholder="Search for your school in Mysore…"
              autoComplete="off"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {selected && (
                <button type="button" onClick={() => { setSelected(null); setSearch(''); }} className="text-slate-400 hover:text-slate-600">
                  <XIcon />
                </button>
              )}
              <span className="text-slate-400"><ChevronDownIcon /></span>
            </div>
          </div>
          {dropdownOpen && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-eco-border rounded-xl shadow-lg max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-400">No schools found</div>
              ) : (
                filtered.map((school) => (
                  <button
                    key={school.id}
                    type="button"
                    onClick={() => { setSelected(school); setSearch(school.name); setDropdownOpen(false); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-emerald-50 transition-colors border-b border-slate-50 last:border-0"
                  >
                    <div className="text-sm font-medium text-slate-800">{school.name}</div>
                    <div className="text-xs text-slate-400">{school.city}{school.address ? ` · ${school.address}` : ''}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowNewSchoolForm(true)}
          className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 font-semibold hover:underline"
        >
          <PlusIcon />
          My school is not listed — Request New School
        </button>
      </div>
    );
  };

  // ── Registration Form ──
  return (
    <div className="min-h-screen grain bg-eco-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2 justify-center">
            <div className="w-10 h-10 rounded-2xl bg-emerald-600 grid place-items-center text-white shadow-md">
              <LeafIcon />
            </div>
            <span className="font-display text-2xl text-emerald-900">We Are Circular</span>
          </Link>
          <p className="mt-2 text-slate-600 text-sm">Request to Join the Platform</p>
        </div>

        <div className="eco-card p-6 sm:p-8">
          {/* Role badge + back button */}
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => { setSelectedRole(null); setError(''); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeftIcon /> Back
            </button>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-semibold ${roleInfo.color}`}>
              <span>{roleInfo.emoji}</span>
              <span className="text-slate-800">{roleInfo.label} Registration</span>
            </div>
          </div>

          {newSchoolSuccess && (
            <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 text-sm">
              ✅ Your school request has been submitted to the Super Admin for review.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Common fields */}
            <div>
              <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Full Name *</label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required
                className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                placeholder="Your full name" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Email Address *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                placeholder="you@example.com" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-emerald-900 mb-1.5">
                Mobile Number <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                placeholder="+91 98765 43210" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Password *</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Min. 6 characters" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Confirm Password *</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Repeat password" />
              </div>
            </div>

            {/* ── Student-specific fields ── */}
            {selectedRole === 'student' && (
              <>
                <SchoolDropdown
                  value={selectedSchool}
                  search={schoolSearch}
                  setSearch={setSchoolSearch}
                  selected={selectedSchool}
                  setSelected={setSelectedSchool}
                  dropdownOpen={schoolDropdownOpen}
                  setDropdownOpen={setSchoolDropdownOpen}
                  dropdownRef={schoolDropdownRef}
                  label="School Name"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Class / Grade *</label>
                    <select value={grade} onChange={(e) => setGrade(e.target.value)} required
                      className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                      <option value="">Select grade</option>
                      {['1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                        <option key={g} value={g}>Grade {g}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Section</label>
                    <input type="text" value={section} onChange={(e) => setSection(e.target.value.toUpperCase())} maxLength={3}
                      className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="e.g. A, B, C" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-1.5">
                    Roll Number <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input type="text" value={rollNumber} onChange={(e) => setRollNumber(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    placeholder="Your roll number" />
                </div>
              </>
            )}

            {/* ── Volunteer-specific fields ── */}
            {selectedRole === 'volunteer' && (
              <>
                <SchoolDropdown
                  value={volunteerSchool}
                  search={volunteerSchoolSearch}
                  setSearch={setVolunteerSchoolSearch}
                  selected={volunteerSchool}
                  setSelected={setVolunteerSchool}
                  dropdownOpen={volunteerSchoolDropdownOpen}
                  setDropdownOpen={setVolunteerSchoolDropdownOpen}
                  dropdownRef={volunteerSchoolDropdownRef}
                  label="School You Are Participating In"
                />
                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Area / Locality *</label>
                  <input type="text" value={volunteerArea} onChange={(e) => setVolunteerArea(e.target.value)} required
                    className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                    placeholder="e.g. Kuvempunagar, Mysuru" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-1.5">
                    Relevant Experience <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <textarea value={volunteerExperience} onChange={(e) => setVolunteerExperience(e.target.value)} rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                    placeholder="Any prior experience in waste management, NGO work, etc." />
                </div>
                <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 text-sm text-teal-700">
                  <p className="font-semibold mb-1">Volunteer Role</p>
                  <p className="text-xs leading-relaxed">As a volunteer, you will verify student waste submissions, manage collection bags, and coordinate dispatch to recyclers. You will be assigned to students from the school you select above.</p>
                </div>
              </>
            )}

            {/* ── Teacher-specific fields ── */}
            {selectedRole === 'teacher' && (
              <>
                <SchoolDropdown
                  value={teacherSchool}
                  search={teacherSchoolSearch}
                  setSearch={setTeacherSchoolSearch}
                  selected={teacherSchool}
                  setSelected={setTeacherSchool}
                  dropdownOpen={teacherSchoolDropdownOpen}
                  setDropdownOpen={setTeacherSchoolDropdownOpen}
                  dropdownRef={teacherSchoolDropdownRef}
                  label="School Name"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Subject Taught</label>
                    <input type="text" value={teacherSubject} onChange={(e) => setTeacherSubject(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                      placeholder="e.g. Science, EVS" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Class / Grade</label>
                    <select value={teacherGrade} onChange={(e) => setTeacherGrade(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                      <option value="">Select grade</option>
                      {['1','2','3','4','5','6','7','8','9','10','11','12'].map(g => (
                        <option key={g} value={g}>Grade {g}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
                  <p className="font-semibold mb-1">Teacher Role</p>
                  <p className="text-xs leading-relaxed">As a teacher, you can monitor your class&apos;s environmental impact, track student progress, view leaderboards, and participate in campaigns.</p>
                </div>
              </>
            )}

            {/* ── School Admin-specific fields ── */}
            {selectedRole === 'school_admin' && (
              <>
                <SchoolDropdown
                  value={adminSchool}
                  search={adminSchoolSearch}
                  setSearch={setAdminSchoolSearch}
                  selected={adminSchool}
                  setSelected={setAdminSchool}
                  dropdownOpen={adminSchoolDropdownOpen}
                  setDropdownOpen={setAdminSchoolDropdownOpen}
                  dropdownRef={adminSchoolDropdownRef}
                  label="School Name"
                />
                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Designation *</label>
                  <select value={adminDesignation} onChange={(e) => setAdminDesignation(e.target.value)} required
                    className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                    <option value="">Select designation</option>
                    <option value="principal">Principal</option>
                    <option value="vice_principal">Vice Principal</option>
                    <option value="headmaster">Headmaster / Headmistress</option>
                    <option value="coordinator">Programme Coordinator</option>
                    <option value="admin_officer">Administrative Officer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 text-sm text-purple-700">
                  <p className="font-semibold mb-1">School Admin Role</p>
                  <p className="text-xs leading-relaxed">As a School Admin, you can approve student registrations, monitor school-wide waste collection, manage volunteers, and generate reports for your school.</p>
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting Request…' : 'Request to Join'}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link href="/login" className="text-emerald-700 font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      {/* New School Request Modal */}
      {showNewSchoolForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="font-display text-lg text-emerald-900">Request New School</h3>
                <p className="text-xs text-slate-500 mt-0.5">This will be sent to the Super Admin for review</p>
              </div>
              <button onClick={() => setShowNewSchoolForm(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <XIcon />
              </button>
            </div>
            <form onSubmit={handleNewSchoolSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">School Name *</label>
                <input type="text" value={newSchoolForm.school_name} onChange={(e) => setNewSchoolForm(f => ({ ...f, school_name: e.target.value }))} required
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Full school name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">School Address *</label>
                <input type="text" value={newSchoolForm.school_address} onChange={(e) => setNewSchoolForm(f => ({ ...f, school_address: e.target.value }))} required
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Street address" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Area / Locality *</label>
                <input type="text" value={newSchoolForm.area_locality} onChange={(e) => setNewSchoolForm(f => ({ ...f, area_locality: e.target.value }))} required
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="e.g. Kuvempunagar, Mysuru" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Contact Person <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="text" value={newSchoolForm.contact_person} onChange={(e) => setNewSchoolForm(f => ({ ...f, contact_person: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="Principal or admin name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-emerald-900 mb-1.5">School Website <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="url" value={newSchoolForm.school_website} onChange={(e) => setNewSchoolForm(f => ({ ...f, school_website: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="https://school.edu.in" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={newSchoolSubmitting}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold text-sm hover:bg-emerald-700 disabled:opacity-60 transition-colors">
                  {newSchoolSubmitting ? 'Submitting…' : 'Submit Request'}
                </button>
                <button type="button" onClick={() => setShowNewSchoolForm(false)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
